import { Component, createContext, useMemo, useContext, useCallback } from 'react';

import classnames from 'classnames'
import { withFormsy } from 'formsy-react'
import { merge } from 'lodash';

const FormFragmentContext = createContext((...names) => names.reduce((prefix, name) => `${prefix}[${name}]`))

export function FormFragment({ name: prefix, back, children }) {
  const nameContext = useContext(FormFragmentContext)
  back = back && [].concat(new Array(back)).fill(true)
  prefix = [].concat(prefix)

  const previousContext = back && back.reduce(context => context.previous || context, nameContext) || nameContext
  const currentConext = previousContext.bind(null, ...prefix)
  currentConext.previous = previousContext

  return <FormFragmentContext.Provider value={currentConext}>
    {children}
  </FormFragmentContext.Provider>
}

export default class FormComponent extends Component {
  get self() {
    return this.constructor
  }

  static contextType = FormFragmentContext

  static useClassnames = ({ className, defaultClassName = '' }, { valid, pristine }) => useMemo(() => classnames(defaultClassName, className, {
    'is-invalid': !valid && !pristine,
  }), [valid, pristine])

  static useFeedback = errorMessage => useMemo(() => {
    if (!errorMessage) {
      return null
    }

    return <span className="invalid-feedback mt-1">
      {errorMessage}
    </span>
  }, [errorMessage])

  static useFormsy = ({ setValue, getValue, isValid, isPristine, getErrorMessage, isRequired }, { onChange }) => {
    const value = useMemo(getValue)
    const pristine = useMemo(isPristine)
    const required = useMemo(isRequired)
    const valid = useMemo(isValid)
    const errorMessage = useMemo(getErrorMessage)

    const handleChange = useCallback(value => {
      setValue(value);
      onChange && onChange(value)
    })

    return {
      onChange: handleChange,
      value,
      pristine,
      required,
      valid,
      errorMessage,
    }
  }

  static get WithFormsy() {
    return ({ currentProps, ...formsyProps }) => {
      const { onChange, value, errorMessage, pristine, valid } = this.useFormsy(formsyProps, currentProps)
      const className = this.useClassnames(currentProps, { pristine, valid })
      const feedbackError = this.useFeedback(errorMessage)

      return <>
        <this.PureComponent {...{ ...currentProps, onChange, className, value }} />
        {feedbackError}
      </>
    }
  }

  static set Pure(Component) {
    this.PureComponent = Component
    this.WithFormsyComponent = withFormsy(this.WithFormsy)
  }

  static set extendDefaultProps(props) {
    this.defaultProps = merge({}, this.defaultProps || {}, props)
  }

  render() {
    const { props: { name, required, validationErrors, validations, ...currentProps }, self: { WithFormsyComponent } } = this
    return <WithFormsyComponent name={this.context(name)}
      required={required}
      currentProps={currentProps}
      validationErrors={validationErrors}
      validations={validations}
      {...currentProps}
    />
  }
}

FormComponent.defaultProps = {
  validationErrors: {
    isDefaultRequiredValue: "Field Required.",
  },
}
